/**
 * Lesson article body — Sprint 2 carry-over (MDX bundling per DEC-0006).
 *
 * The backend stores each lesson's ``article_mdx_path`` as a repo-relative
 * key like ``content/module-1-cait-foundations/lessons/1-global-mental-health-crisis.mdx``.
 * The frontend bundles every ``.mdx`` file under ``content/`` at build time
 * via ``import.meta.glob`` + ``@mdx-js/rollup``, lazy-loaded so a course
 * with 50 lessons doesn't ship 50 components in the initial bundle.
 *
 * Authored lessons currently use one JSX component, ``<Callout>``. The
 * MDX provider supplies it (and any future bespoke components) via the
 * ``components`` prop on ``MDXProvider``.
 *
 * Fallbacks:
 * - If the path doesn't resolve in the bundle map (content authored
 *   after the last frontend build), render a clear "content not in
 *   this build" notice instead of crashing.
 * - If MDX runtime errors during render, swallow + render a placeholder
 *   so the rest of the lesson player keeps working.
 */
import { MDXProvider } from "@mdx-js/react";
import * as React from "react";
import { Suspense, lazy, useMemo } from "react";

// Vite-resolved glob: lazy-loaded MDX components for every lesson under
// content/. The keys are paths relative to this file (`../../`) plus the
// content tree. We normalize the article_mdx_path coming from the API
// against these keys at render time.
// MDX component overrides aren't easily typeable (each element gets its
// own HTML props), so the ``components`` prop is typed loosely. Vite
// resolves the actual implementations at build time.
type MDXComponents = Record<string, React.ComponentType<any>>;  // eslint-disable-line @typescript-eslint/no-explicit-any

interface MDXModule {
  default: React.ComponentType<{ components?: MDXComponents }>;
}

const mdxModules = import.meta.glob<MDXModule>("../../../content/**/*.mdx");

// ---------------------------------------------------------------------------
// Custom MDX components
// ---------------------------------------------------------------------------

interface CalloutProps {
  type?: "key-point" | "note" | "warning";
  children?: React.ReactNode;
}

function Callout({ type = "note", children }: CalloutProps): React.JSX.Element {
  const variant =
    type === "key-point"
      ? "border-emerald-300 bg-emerald-50 text-emerald-900"
      : type === "warning"
        ? "border-amber-300 bg-amber-50 text-amber-900"
        : "border-border bg-background text-foreground";
  const label =
    type === "key-point"
      ? "Key point"
      : type === "warning"
        ? "Watch out"
        : "Note";
  return (
    <aside
      className={`my-6 rounded-lg border-l-4 px-4 py-3 ${variant}`}
      role="note"
    >
      <p className="mb-1 text-xs font-semibold uppercase tracking-wider">
        {label}
      </p>
      <div className="text-sm leading-relaxed">{children}</div>
    </aside>
  );
}

// Element-level overrides so the MDX renders with our visual style.
// Tailwind 4 doesn't ship @tailwindcss/typography by default; declaring
// the per-element classes here avoids the extra dep.
//
// The lesson page header already renders the lesson title as the page's
// only ``<h1>`` (LessonPlayer.tsx). Authored MDX often starts with a
// ``# Title`` line, which would duplicate the h1 in the rendered tree
// and harm a11y + SEO. We map MDX ``h1`` → semantic ``<h2>`` so the
// page has exactly one h1 regardless of what authors write.
function H1(props: React.ComponentPropsWithoutRef<"h1">): React.JSX.Element {
  return (
    <h2
      {...props}
      className="mt-8 text-2xl font-bold tracking-tight text-foreground"
    />
  );
}
function H2(props: React.ComponentPropsWithoutRef<"h2">): React.JSX.Element {
  return (
    <h2
      {...props}
      className="mt-8 text-xl font-semibold tracking-tight text-foreground"
    />
  );
}
function H3(props: React.ComponentPropsWithoutRef<"h3">): React.JSX.Element {
  return (
    <h3
      {...props}
      className="mt-6 text-lg font-semibold text-foreground"
    />
  );
}
function P(props: React.ComponentPropsWithoutRef<"p">): React.JSX.Element {
  return (
    <p {...props} className="mt-4 text-base leading-relaxed text-foreground" />
  );
}
function UL(props: React.ComponentPropsWithoutRef<"ul">): React.JSX.Element {
  return (
    <ul
      {...props}
      className="mt-4 list-disc space-y-2 pl-6 text-foreground"
    />
  );
}
function OL(props: React.ComponentPropsWithoutRef<"ol">): React.JSX.Element {
  return (
    <ol
      {...props}
      className="mt-4 list-decimal space-y-2 pl-6 text-foreground"
    />
  );
}
function A(props: React.ComponentPropsWithoutRef<"a">): React.JSX.Element {
  return (
    <a
      {...props}
      className="font-semibold text-foreground underline underline-offset-2 hover:text-foreground"
    />
  );
}
function Strong(
  props: React.ComponentPropsWithoutRef<"strong">,
): React.JSX.Element {
  return <strong {...props} className="font-semibold text-foreground" />;
}
function Blockquote(
  props: React.ComponentPropsWithoutRef<"blockquote">,
): React.JSX.Element {
  return (
    <blockquote
      {...props}
      className="mt-4 border-l-4 border-border pl-4 text-muted-foreground italic"
    />
  );
}

const mdxComponents = {
  h1: H1,
  h2: H2,
  h3: H3,
  p: P,
  ul: UL,
  ol: OL,
  a: A,
  strong: Strong,
  blockquote: Blockquote,
  Callout,
  // Future bespoke components (<Citation>, <Spotlight>, <ReflectionPrompt>)
  // declared in Design §4.1 land here as the content team adds them.
};

// ---------------------------------------------------------------------------
// Bundle key resolution
// ---------------------------------------------------------------------------

/** Convert the API's ``article_mdx_path`` value into the glob's import key.
 *  The API returns ``content/module-1-.../lessons/N-slug.mdx`` (repo-relative).
 *  Vite's ``import.meta.glob`` keys this file relative to *this* TS source
 *  file: ``../../../content/...`` since LessonBody.tsx is at
 *  ``frontend/src/components/``. */
function resolveBundleKey(articleMdxPath: string): string | null {
  // Strip a leading "content/" because the glob key already includes it.
  const normalized = articleMdxPath.startsWith("content/")
    ? articleMdxPath
    : `content/${articleMdxPath}`;
  const key = `../../../${normalized}`;
  return key in mdxModules ? key : null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface LessonBodyProps {
  /** Repo-relative MDX path as returned by ``GET /api/lessons/{id}``. */
  articleMdxPath: string;
}

export function LessonBody({
  articleMdxPath,
}: LessonBodyProps): React.JSX.Element {
  // Both memos are computed unconditionally to satisfy the rules of
  // hooks. ``LessonComponent`` is null when the path doesn't resolve in
  // the bundle, which the render branch handles below.
  const bundleKey = useMemo(
    () => resolveBundleKey(articleMdxPath),
    [articleMdxPath],
  );
  const LessonComponent = useMemo(
    () => (bundleKey === null ? null : lazy(mdxModules[bundleKey]!)),
    [bundleKey],
  );

  if (LessonComponent === null) {
    return (
      <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-6 text-sm text-amber-900">
        <p className="font-semibold">Lesson body not bundled with this build.</p>
        <p className="mt-2">
          The MDX file at <code>{articleMdxPath}</code> wasn&apos;t found in
          the frontend&apos;s content bundle. This usually means content
          was authored after the last deploy.
        </p>
      </div>
    );
  }

  return (
    <MDXProvider components={mdxComponents}>
      <article className="max-w-none" data-testid="lesson-body">
        <Suspense
          fallback={
            <p className="text-sm text-muted-foreground" role="status">
              Loading lesson body…
            </p>
          }
        >
          <LessonComponent components={mdxComponents} />
        </Suspense>
      </article>
    </MDXProvider>
  );
}
